import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  BriefcaseBusiness,
  CircleDot,
  Code2,
  Cpu,
  Dumbbell,
  Eye,
  GraduationCap,
  Layers3,
  ListChecks,
  Mail,
  MapPinned,
  Martini,
  MessageCircle,
  Minus,
  Network,
  Phone,
  Plus,
  Rocket,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Utensils,
  UsersRound,
} from "lucide-react";
import "../main-studio.css";
import "../runtime.css";

const systems = [
  {
    id: "orangecity",
    name: "OrangeCity Go",
    shortName: "OrangeCity Go",
    type: "Nagpur City OS",
    description: "Everything Nagpur needs in one place—from food and local shopping to bookings, services and city discovery.",
    icon: MapPinned,
    href: null,
    state: "coming",
    signal: "#ff7426",
  },
  {
    id: "resto",
    name: "WillovateResto",
    shortName: "Resto",
    type: "Restaurant OS",
    description: "Orders, inventory, menu operations, payments, automation and analytics.",
    icon: Utensils,
    href: "https://resto.willovate.in/",
    state: "live",
    signal: "#ff6b35",
  },
  {
    id: "task",
    name: "Willovate Task",
    shortName: "Task",
    type: "Work OS",
    description: "Boards, backlogs, sprints, milestones and visible team delivery.",
    icon: ListChecks,
    href: "https://task.willovate.in/",
    state: "live",
    signal: "#2161ff",
  },
  {
    id: "gym",
    name: "Willovate Gym",
    shortName: "Gym",
    type: "Fitness OS",
    description: "Memberships, attendance, trainers, leads and WhatsApp engagement.",
    icon: Dumbbell,
    href: "https://gym.willovate.in/",
    state: "live",
    signal: "#ff6f32",
  },
  {
    id: "wbm",
    name: "WBM — Bar Management",
    shortName: "WBM",
    type: "Business OS",
    description: "Counter sheets, stock, billing, purchases, shifts and AI reports.",
    icon: Martini,
    href: "https://wbm.willovate.in/",
    state: "live",
    signal: "#1469ff",
  },
  {
    id: "labs",
    name: "Willovate Labs",
    shortName: "Labs",
    type: "Learning engine",
    description: "Programs, internships, hackathons and real product practice.",
    icon: GraduationCap,
    href: "https://labs.willovate.in",
    state: "active",
    signal: "#00aebb",
  },
  {
    id: "zukhire",
    name: "Zukhire",
    shortName: "Zukhire",
    type: "Talent OS",
    description: "A future talent and opportunity system planned for the Willovate ecosystem.",
    icon: UsersRound,
    href: null,
    state: "coming",
    signal: "#8b93a7",
  },
];

const processStages = [
  { title: "Observe", copy: "We begin with the real workflow—people, decisions, bottlenecks and the signals already present.", icon: Eye, metric: "Reality before features" },
  { title: "Design", copy: "We shape the system around how work should move, keeping every interaction purposeful and clear.", icon: ScanLine, metric: "Human-centered flows" },
  { title: "Build", copy: "Composable architecture and automation create a foundation that can change without losing control.", icon: Code2, metric: "Modular by default" },
  { title: "Ship", copy: "Real teams use the system. Feedback moves directly into product decisions, rollout and support.", icon: Rocket, metric: "Impact from day one" },
  { title: "Evolve", copy: "The product learns from use. New capability arrives when the business is ready for it.", icon: Sparkles, metric: "Continuous improvement" },
];

const architectureLayers = [
  { title: "Experience", copy: "Role-based web and mobile interfaces", icon: UsersRound, meta: "UI / ACCESS" },
  { title: "Workflow core", copy: "Business rules, states and approvals", icon: Network, meta: "LOGIC / STATE" },
  { title: "Automation", copy: "Events, triggers, queues and integrations", icon: Cpu, meta: "EVENT / API" },
  { title: "Operational data", copy: "One dependable source of business truth", icon: Layers3, meta: "DATA / AUDIT" },
  { title: "Secure foundation", copy: "Identity, permissions and infrastructure", icon: ShieldCheck, meta: "SECURE / SCALE" },
];

const capabilities = [
  { title: "AI where useful", text: "Signals, reporting and automation in the moments they improve a decision.", icon: Cpu },
  { title: "Secure by design", text: "Clear ownership, controlled access and dependable business data.", icon: ShieldCheck },
  { title: "Connected thinking", text: "Separate products shaped by one consistent operating philosophy.", icon: Network },
  { title: "Ready to extend", text: "Modular systems that can grow without rebuilding the foundation.", icon: Layers3 },
];

const faqs = [
  ["Which Willovate products are live?", "WillovateResto, Willovate Task, Willovate Gym and WBM are the live SaaS products currently presented in our main portfolio."],
  ["What is OrangeCity Go?", "OrangeCity Go is an upcoming Nagpur-first city platform for food, local shopping, bookings, services and discovering what the city offers. It is not live yet."],
  ["Is Zukhire available now?", "No. Zukhire is a coming-soon talent system and is shown only as an upcoming signal. It has no launch link or live-product status."],
  ["How does Willovate Labs fit in?", "Labs is Willovate’s separate learning arm for career programs, internships, hackathons and practical product-building experience."],
  ["Can Willovate adapt a product to our workflow?", "Yes. We configure roles, workflows, reporting and integrations around the way your team operates. When configuration is not enough, we can scope a custom system."],
  ["What happens after launch?", "We stay involved through rollout, feedback, support, performance and future workflow improvements."],
];

const railItems = [
  ["home", "Home"],
  ["ecosystem", "Ecosystem"],
  ["philosophy", "Method"],
  ["products", "Products"],
  ["labs", "Labs"],
  ["contact", "Contact"],
];

const networkParticles = Array.from({ length: 42 }, (_, index) => ({
  x: 10 + ((index * 37) % 80),
  y: 8 + ((index * 53) % 84),
  delay: (index % 11) * -0.43,
  duration: 3.4 + (index % 7) * 0.55,
  accent: index % 9 === 0,
}));

const workflowLanes = [
  { input: "People", inputMeta: "Needs & intent", output: "Clear decisions", outputMeta: "What happens next", icon: UsersRound, signal: "#ff6a2b", path: "M170 132 C300 132 325 225 440 244 C515 257 574 251 650 222 C760 180 780 132 910 132" },
  { input: "Operations", inputMeta: "Live business data", output: "Automation", outputMeta: "Routine work handled", icon: Activity, signal: "#115cff", path: "M170 286 C310 286 348 286 448 286 C548 286 690 286 910 286" },
  { input: "Teams", inputMeta: "Work in motion", output: "Visible action", outputMeta: "Owned and measurable", icon: ListChecks, signal: "#00aebb", path: "M170 440 C300 440 325 347 440 328 C515 315 574 321 650 350 C760 392 780 440 910 440" },
];

const SystemDestination = ({ system, className, children }) => {
  if (!system.href) return <div className={`${className} is-disabled`} aria-disabled="true">{children}</div>;
  if (system.href.startsWith("http")) {
    return <a href={system.href} className={className} target="_blank" rel="noopener noreferrer">{children}</a>;
  }
  return <Link to={system.href} className={className}>{children}</Link>;
};

function RuntimeFlow({ interactive = false }) {
  const networkRef = useRef(null);

  const handlePointerMove = (event) => {
    if (!interactive || !networkRef.current) return;
    const rect = networkRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    networkRef.current.style.setProperty("--runtime-x", `${(x * 9).toFixed(2)}px`);
    networkRef.current.style.setProperty("--runtime-y", `${(y * 9).toFixed(2)}px`);
  };

  const clearPointer = () => {
    if (!networkRef.current) return;
    networkRef.current.style.setProperty("--runtime-x", "0px");
    networkRef.current.style.setProperty("--runtime-y", "0px");
  };

  return (
    <div className="runtime-flow" ref={networkRef} onPointerMove={handlePointerMove} onPointerLeave={clearPointer}>
      <div className="runtime-flow-grid" />
      <div className="runtime-network-particles" aria-hidden="true">
        {networkParticles.map((particle, index) => (
          <i
            key={index}
            className={particle.accent ? "is-accent" : ""}
            style={{
              "--particle-x": `${particle.x}%`,
              "--particle-y": `${particle.y}%`,
              "--particle-delay": `${particle.delay}s`,
              "--particle-duration": `${particle.duration}s`,
            }}
          />
        ))}
      </div>
      <svg className="runtime-flow-routes" viewBox="0 0 1080 570" aria-hidden="true">
        <defs>
          <filter id="runtime-flow-glow"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>
        {workflowLanes.map((lane, index) => (
          <g key={lane.input} className="runtime-flow-route">
            <path id={`runtime-flow-route-${index}`} d={lane.path} />
            <circle r="4" fill={lane.signal} filter="url(#runtime-flow-glow)">
              <animateMotion dur={`${4.7 + index * 0.65}s`} begin={`${index * -1.1}s`} repeatCount="indefinite">
                <mpath href={`#runtime-flow-route-${index}`} />
              </animateMotion>
            </circle>
            <circle r="2.5" fill="#115cff">
              <animateMotion dur={`${6.1 + index * 0.5}s`} begin={`${index * -1.7 - 2}s`} repeatCount="indefinite">
                <mpath href={`#runtime-flow-route-${index}`} />
              </animateMotion>
            </circle>
          </g>
        ))}
      </svg>

      <div className="runtime-flow-inputs">
        <small>01 / Signals enter</small>
        {workflowLanes.map((lane, index) => {
          const Icon = lane.icon;
          return <article key={lane.input}><span>0{index + 1}</span><div style={{ "--flow-signal": lane.signal }}><Icon size={17} /></div><strong>{lane.input}</strong><small>{lane.inputMeta}</small></article>;
        })}
      </div>

      <div className="runtime-flow-engine">
        <header><span><i /> Willovate runtime</span><small>Processing / live</small></header>
        <div className="runtime-flow-engine-body">
          <div className="runtime-flow-scan" />
          <span className="runtime-flow-bracket bracket-a" /><span className="runtime-flow-bracket bracket-b" />
          <img src="/willovate-mark.png" alt="" />
          <strong>WORK FABRIC</strong>
          <small>OBSERVE · CONNECT · DECIDE · ACT</small>
        </div>
        <footer><span>Latency</span><strong>12ms</strong><span>Flow state</span><strong>Nominal</strong></footer>
      </div>

      <div className="runtime-flow-outputs">
        <small>03 / Useful outcomes</small>
        {workflowLanes.map((lane, index) => <article key={lane.output}><span>0{index + 1}</span><i style={{ "--flow-signal": lane.signal }} /><strong>{lane.output}</strong><small>{lane.outputMeta}</small></article>)}
      </div>

      <div className="runtime-flow-telemetry"><span>INPUT / CONTINUOUS</span><span>ENGINE / COMPOSABLE</span><span>OUTPUT / MEASURABLE</span></div>
    </div>
  );
}

function EcosystemConstellation() {
  const constellationRoutes = [
    "M500 330 C405 255 315 180 190 118",
    "M500 330 C595 255 685 180 810 118",
    "M500 330 C365 330 250 330 112 330",
    "M500 330 C635 330 750 330 888 330",
    "M500 330 C400 395 325 475 230 535",
    "M500 330 C600 395 675 475 770 535",
    "M500 330 C500 245 500 165 500 75",
  ];

  return (
    <div className="runtime-constellation" aria-label="Willovate product constellation">
      <div className="runtime-constellation-grid" aria-hidden="true" />
      <svg viewBox="0 0 1000 640" aria-hidden="true">
        <defs>
          <filter id="constellation-glow"><feGaussianBlur stdDeviation="2.5" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>
        <circle className="constellation-scan scan-a" cx="500" cy="330" r="150" />
        <circle className="constellation-scan scan-b" cx="500" cy="330" r="245" />
        {systems.map((system, index) => (
          <g key={system.id} className={`constellation-route route-${system.state}`}>
            <path id={`constellation-route-${system.id}`} d={constellationRoutes[index]} />
            {system.state !== "coming" ? (
              <circle r="4" fill={system.signal} filter="url(#constellation-glow)">
                <animateMotion dur={`${4.5 + index * 0.45}s`} begin={`${index * -0.8}s`} repeatCount="indefinite">
                  <mpath href={`#constellation-route-${system.id}`} />
                </animateMotion>
              </circle>
            ) : null}
          </g>
        ))}
      </svg>
      <div className="runtime-constellation-core">
        <span /><span />
        <div><img src="/willovate-mark.png" alt="" /><strong>WILLOVATE</strong><small>ONE RUNTIME</small></div>
      </div>
      {systems.map((system, index) => {
        const Icon = system.icon;
        return (
          <SystemDestination key={system.id} system={system} className={`constellation-node constellation-${system.id}`}>
            <span className="constellation-node-ring" style={{ "--constellation-signal": system.signal }}><Icon size={22} /></span>
            <strong>{system.shortName}</strong>
            <small>{system.type}</small>
            <i>{system.state === "coming" ? "Coming soon" : system.state}</i>
            <b>0{index + 1}</b>
          </SystemDestination>
        );
      })}
      <div className="runtime-constellation-readout readout-left"><span>SYSTEMS MAPPED</span><strong>07</strong></div>
      <div className="runtime-constellation-readout readout-right"><span>LIVE PRODUCTS</span><strong>04</strong></div>
    </div>
  );
}

function RuntimeRail({ activeSection, onMove }) {
  return (
    <aside className="runtime-side-rail" aria-label="Story navigation">
      <div className="runtime-rail-brand">W</div>
      <nav>
        {railItems.map(([id, label], index) => (
          <button type="button" key={id} className={activeSection === id ? "is-active" : ""} onClick={() => onMove(id)} aria-label={`Go to ${label}`}>
            <span>0{index + 1}</span><small>{label}</small>
          </button>
        ))}
      </nav>
      <div className="runtime-rail-location"><span>NAGPUR</span><small>21.1458° N</small></div>
    </aside>
  );
}

function ProcessMachine({ activeStage }) {
  return (
    <div className={`runtime-process-machine stage-${activeStage + 1}`}>
      <div className="runtime-machine-head"><span><i /> System model</span><small>Composed / 05 layers</small></div>
      <div className="runtime-machine-canvas">
        <div className="runtime-architecture-map">
          <div className="runtime-architecture-entry"><span>User intent</span><i /><strong>INPUT</strong></div>
          <div className="runtime-architecture-stack">
            {architectureLayers.map((layer, index) => {
              const Icon = layer.icon;
              return (
                <article key={layer.title} className={`architecture-layer architecture-layer-${index + 1}`}>
                  <span>0{5 - index}</span>
                  <div><Icon size={19} /></div>
                  <section><strong>{layer.title}</strong><small>{layer.copy}</small></section>
                  <b>{layer.meta}</b>
                  <i className="architecture-port" />
                </article>
              );
            })}
          </div>
          <div className="runtime-architecture-output"><span>Measurable action</span><i /><strong>OUTPUT</strong></div>
          <div className="runtime-architecture-bus" aria-hidden="true"><i /><i /><i /></div>
          <div className="runtime-architecture-readout"><span>EVENT BUS</span><strong>CONNECTED</strong><span>DATA STATE</span><strong>CONSISTENT</strong></div>
        </div>
      </div>
      <div className="runtime-machine-footer"><span>{processStages[activeStage].metric}</span><strong>BUILD FOCUS / {processStages[activeStage].title}</strong></div>
    </div>
  );
}

const MainLanding = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [activeSection, setActiveSection] = useState("home");
  const [activeStage, setActiveStage] = useState(0);
  const progressRef = useRef(null);

  const moveTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    const updateProgress = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${progress})`;
    };
    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    return () => window.removeEventListener("scroll", updateProgress);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActiveSection(visible.target.id);
    }, { rootMargin: "-25% 0px -58% 0px", threshold: [0, 0.1, 0.35] });
    railItems.forEach(([id]) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveStage(Number(entry.target.dataset.runtimeStage));
      });
    }, { rootMargin: "-38% 0px -46% 0px", threshold: 0.1 });
    const stages = document.querySelectorAll("[data-runtime-stage]");
    stages.forEach((stage) => observer.observe(stage));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="wv-runtime-main">
      <div className="runtime-scroll-progress" ref={progressRef} />
      <RuntimeRail activeSection={activeSection} onMove={moveTo} />

      <section id="home" className="runtime-hero runtime-panel">
        <div className="runtime-hero-meta">
          <span>WILLOVATE / RUNTIME 01</span>
          <div><i /> All live systems nominal</div>
        </div>
        <div className="runtime-hero-heading">
          <div>
            <span className="runtime-eyebrow"><i /> Software ecosystem · India</span>
            <h1>Software for the way<br />work actually <em>moves.</em></h1>
          </div>
          <aside><small>System status</small><strong>04 live products</strong><span>01 learning arm</span><span>02 coming soon</span></aside>
        </div>
        <div className="runtime-hero-network"><RuntimeFlow interactive /></div>
        <div className="runtime-hero-bottom">
          <button type="button" onClick={() => moveTo("ecosystem")}><span>Enter the system</span><ArrowDown size={18} /></button>
          <p>Built in Nagpur. Designed for real operations, real teams and decisions that cannot wait.</p>
          <div><span>Runtime</span><strong>01.00</strong><span>Status</span><strong>Nominal</strong></div>
        </div>
      </section>

      <section id="ecosystem" className="runtime-ecosystem runtime-panel">
        <div className="runtime-section-heading">
          <span>01 / Ecosystem</span>
          <h2>One company.<br />Many focused systems<br />in <em>motion.</em></h2>
          <p>Willovate is not one oversized application. Each product solves a specific operational problem and keeps its own focused experience.</p>
        </div>
        <EcosystemConstellation />
        <div className="runtime-system-directory">
          {systems.map((system, index) => {
            const Icon = system.icon;
            return (
              <SystemDestination key={system.id} system={system} className={`runtime-directory-card directory-${system.state}`}>
                <div className="runtime-directory-top"><span>0{index + 1}</span><small className={`state-${system.state}`}><i /> {system.state === "coming" ? "Coming soon" : system.state}</small></div>
                <div className="runtime-directory-icon" style={{ "--directory-signal": system.signal }}><Icon size={25} /></div>
                <small>{system.type}</small>
                <h3>{system.name}</h3>
                <p>{system.description}</p>
                <div className="runtime-directory-action"><span>{system.state === "coming" ? "In development" : system.state === "active" ? "Explore Labs" : "Open product"}</span>{system.href ? <ArrowUpRight size={17} /> : <CircleDot size={15} />}</div>
              </SystemDestination>
            );
          })}
        </div>
        <div className="runtime-ecosystem-note"><Activity size={17} /><span><strong>Honest system state</strong> Live, active and upcoming products are labelled separately.</span></div>
      </section>

      <section id="philosophy" className="runtime-process runtime-panel">
        <div className="runtime-process-copy">
          <div className="runtime-process-title">
            <span>02 / How we build</span>
            <h2>Built as products.<br />Evolved as <em>systems.</em></h2>
            <p>Scroll through the operating method. Every layer becomes active only when the previous one has a clear reason to exist.</p>
          </div>
          <div className="runtime-process-stages">
            {processStages.map((stage, index) => {
              const Icon = stage.icon;
              return (
                <article key={stage.title} data-runtime-stage={index} className={activeStage === index ? "is-active" : ""} onMouseEnter={() => setActiveStage(index)}>
                  <span>0{index + 1}</span><div><Icon size={20} /></div><h3>{stage.title}</h3><p>{stage.copy}</p><small>{stage.metric}</small>
                </article>
              );
            })}
          </div>
        </div>
        <div className="runtime-process-visual"><ProcessMachine activeStage={activeStage} /></div>
      </section>

      <section id="products" className="runtime-capabilities runtime-panel">
        <div className="runtime-capability-heading">
          <span>03 / Runtime principles</span>
          <h2>A shared standard<br />behind every <em>product.</em></h2>
          <p>The interfaces may be different. The principles beneath them stay consistent.</p>
        </div>
        <div className="runtime-capability-grid">
          {capabilities.map((item, index) => {
            const Icon = item.icon;
            return <article key={item.title}><span>0{index + 1}</span><div><Icon size={23} /></div><h3>{item.title}</h3><p>{item.text}</p></article>;
          })}
        </div>
        <Link to="/products" className="runtime-outline-button">View the live product portfolio <ArrowRight size={18} /></Link>
      </section>

      <section id="labs" className="runtime-labs runtime-panel">
        <div className="runtime-labs-signal"><span><GraduationCap size={28} /></span><i /><span><Code2 size={28} /></span><i /><span><BriefcaseBusiness size={28} /></span></div>
        <div className="runtime-labs-copy">
          <span>04 / Separate learning arm</span>
          <h2>Willovate Labs.<br />Learn by building<br /><em>something real.</em></h2>
          <p>Career programs, internships, hackathons and product work live in their own experience—connected to Willovate, but designed for learners.</p>
          <a href="https://labs.willovate.in" target="_blank" rel="noopener noreferrer" className="runtime-dark-button">Enter Willovate Labs <ArrowRight size={18} /></a>
        </div>
        <div className="runtime-labs-list">
          <a href="https://labs.willovate.in" target="_blank" rel="noopener noreferrer"><span>01</span><div><small>Structured learning</small><strong>Career programs</strong></div><ArrowUpRight size={18} /></a>
          <a href="https://labs.willovate.in" target="_blank" rel="noopener noreferrer"><span>02</span><div><small>Industry experience</small><strong>Internships</strong></div><ArrowUpRight size={18} /></a>
          <a href="https://labs.willovate.in" target="_blank" rel="noopener noreferrer"><span>03</span><div><small>Build in public</small><strong>Hackathons &amp; build days</strong></div><ArrowUpRight size={18} /></a>
        </div>
      </section>

      <section className="runtime-faq runtime-panel">
        <div><span>05 / Clear answers</span><h2>System notes<br />before we <em>talk.</em></h2><p>Real status, real scope and the practical next step.</p></div>
        <div className="runtime-faq-list">
          {faqs.map(([question, answer], index) => (
            <article key={question} className={openFaq === index ? "is-open" : ""}>
              <button type="button" onClick={() => setOpenFaq(openFaq === index ? -1 : index)} aria-expanded={openFaq === index}><span>0{index + 1}</span><strong>{question}</strong>{openFaq === index ? <Minus size={18} /> : <Plus size={18} />}</button>
              {openFaq === index ? <p>{answer}</p> : null}
            </article>
          ))}
        </div>
      </section>

      <section id="contact" className="runtime-contact runtime-panel">
        <div className="runtime-contact-mark"><img src="/willovate-mark.png" alt="" /><span>WILLOVATE<br />RUNTIME 01</span></div>
        <div className="runtime-contact-copy"><span>06 / Start here</span><h2>Have a workflow<br />that should work<br /><em>better?</em></h2></div>
        <div className="runtime-contact-action"><p>Tell us what is slowing the business down. We’ll help identify the right product, custom system or next move.</p><a href="mailto:admin@willovate.in">Start a conversation <ArrowRight size={18} /></a><div><a href="tel:+919322346482"><Phone size={14} /> Call</a><a href="mailto:admin@willovate.in"><Mail size={14} /> Email</a><a href="https://wa.me/919322346482"><MessageCircle size={14} /> WhatsApp</a></div></div>
      </section>
    </main>
  );
};

export default MainLanding;
